import React, { useState } from "react";
import {
	CartesianGrid,
	ComposedChart,
	Customized,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from "recharts";
import "./line-chart-generic.css";

const defaultFormatValue = value => value;

const ChartBackgroundAreas = ({ areas = [] }) => {
	return props => {
		const { offset, yAxisMap } = props;
		const yAxis = yAxisMap?.left;

		if (!offset || !yAxis?.scale) {
			return null;
		}

		const gradientAreas = areas.filter(area => area.gradient);

		return (
			<g>
				{gradientAreas.length > 0 && (
					<defs>
						{gradientAreas.map(area => (
							<linearGradient
								key={area.gradient.id}
								id={area.gradient.id}
								x1="0"
								y1="1"
								x2="0"
								y2="0"
							>
								<stop offset="0%" stopColor={area.gradient.from} />
								<stop offset="100%" stopColor={area.gradient.to} />
							</linearGradient>
						))}
					</defs>
				)}

				{areas.map(area => {
					const yTop = yAxis.scale(area.y2);
					const yBottom = yAxis.scale(area.y1);

					return (
						<rect
							key={area.key}
							x={offset.left}
							y={yTop}
							width={offset.width}
							height={yBottom - yTop}
							fill={
								area.gradient
									? `url(#${area.gradient.id})`
									: area.fill
							}
							fillOpacity={area.fillOpacity ?? 1}
						/>
					);
				})}
			</g>
		);
	};
};

const CustomDot = props => {
	const {
		cx,
		cy,
		value,
		payload,
		seriesConfig
	} = props;

	const currentValue = value ?? payload?.[seriesConfig.dataKey];

	if (
		cx === undefined ||
		cy === undefined ||
		currentValue === undefined ||
		currentValue === null ||
		currentValue === ""
	) {
		return null;
	}

	const dotConfig = typeof seriesConfig.dot === "function"
		? seriesConfig.dot(payload)
		: seriesConfig.dot || {};

	return (
		<circle
			cx={cx}
			cy={cy}
			r={dotConfig.radius || 7}
			fill={dotConfig.fill || seriesConfig.color || "#00518b"}
			stroke={dotConfig.stroke || seriesConfig.color || "#00518b"}
			strokeWidth={dotConfig.strokeWidth || 2}
		/>
	);
};

const getOriginalLabelValue = ({
	value,
	payload,
	dataKey
}) => {
	return (
		payload?.[`${dataKey}Label`] ??
		payload?.[`${dataKey}Original`] ??
		value
	);
};

const PointLabel = props => {
	const {
		x,
		y,
		value,
		payload,
		index,
		seriesConfig,
		chartData = []
	} = props;

	if (
		x === undefined ||
		y === undefined ||
		value === undefined ||
		value === null
	) {
		return null;
	}

	const currentPayload = payload || chartData[index] || {};
	const formatter = seriesConfig.labelFormatter || defaultFormatValue;

	const displayValue = getOriginalLabelValue({
		value,
		payload: currentPayload,
		dataKey: seriesConfig.dataKey
	});

	return (
		<foreignObject x={x - 36} y={y + 12} width={72} height={26}>
			<div className="generic-line-chart__point-label">
				{formatter(displayValue, currentPayload)}
			</div>
		</foreignObject>
	);
};

const getTooltipValue = item => {
	return (
		item.payload?.[`${item.dataKey}Label`] ??
		item.payload?.[`${item.dataKey}Original`] ??
		item.value
	);
};

const CustomTooltip = ({
	active,
	payload,
	label,
	valueFormatter
}) => {
	if (!active || !payload?.length) {
		return null;
	}

	const currentPayload = payload[0]?.payload;

	return (
		<div className="generic-line-chart__tooltip">
			<strong>
				{currentPayload?.month || label}
			</strong>

			{payload.map(item => (
				<span key={item.dataKey}>
					{item.name}: {valueFormatter(getTooltipValue(item))}
				</span>
			))}
		</div>
	);
};

const ReferenceLineLabel = props => {
	const {
		viewBox,
		value,
		position = "left",
		fill = "#000000",
		fontSize = 10,
		fontWeight = 400,
		offset = 8,
		verticalOffset = 0,
		tooltip,
		tooltipClassName,
		onTooltipShow,
		onTooltipHide
	} = props;

	if (!viewBox || !value) {
		return null;
	}

	const isRight = position === "right";

	const x = isRight
		? viewBox.x + viewBox.width + offset
		: viewBox.x - offset;

	const y = viewBox.y + verticalOffset;

	const handleMouseEnter = () => {
		if (!tooltip) return;

		onTooltipShow({
			x,
			y,
			text: tooltip,
			className: tooltipClassName
		});
	};

	return (
		<g
			onMouseEnter={handleMouseEnter}
			onMouseLeave={onTooltipHide}
			style={{ cursor: tooltip ? "help" : "default", pointerEvents: "all" }}
		>
			{verticalOffset !== 0 && (
				<line
					x1={
						isRight
							? viewBox.x + viewBox.width
							: viewBox.x
					}
					y1={viewBox.y}
					x2={isRight ? x - 4 : x + 4}
					y2={y}
					stroke={fill}
					strokeWidth={1}
					strokeOpacity={0.45}
				/>
			)}
			
			<text
				x={x}
				y={y}
				fill={fill}
				fontSize={fontSize}
				fontWeight={fontWeight}
				textAnchor={isRight ? "start" : "end"}
				dominantBaseline="middle"
			>
				{value}
			</text>
		</g>
	);
};

const LineChartGeneric = ({
	data = [],
	height = 360,
	xAxis = {},
	yAxisLeft = {},
	yAxisRight,
	areas = [],
	referenceLines = [],
	series = [],
	legend = [],
	valueFormatter = defaultFormatValue,
	className = "",
	margin
}) => {
	const [isTooltipActive, setIsTooltipActive] = useState(false);
	const [referenceTooltip, setReferenceTooltip] = useState(null);

	const chartMargin = margin || {
		top: 36,
		right: yAxisRight ? 110 : 32,
		bottom: 28,
		left: 100
	};

	const xAxisPaddingLeft = Number(xAxis?.padding?.left || 0);
	const legendDotSize = 16;
	const legendOffset = chartMargin.left + xAxisPaddingLeft - legendDotSize / 2;

	return (
		<div className={`generic-line-chart ${className}`}>
			<ResponsiveContainer width="100%" height={height}>
				<ComposedChart
					data={data}
					margin={chartMargin}
					onMouseMove={state => {
						setIsTooltipActive(Boolean(state?.isTooltipActive));
					}}
					onMouseLeave={() => {
						setIsTooltipActive(false);
					}}
				>
					<Customized component={ChartBackgroundAreas({ areas })} />

					<CartesianGrid
						stroke={xAxis.gridColor || "#8a8a8a"}
						strokeDasharray={xAxis.gridDash || "4 3"}
						vertical={xAxis.verticalGrid !== false}
					/>

					<XAxis
						dataKey={xAxis.dataKey || "label"}
						type={xAxis.type || "category"}
						domain={xAxis.domain}
						scale={xAxis.scale}
						ticks={xAxis.ticks}
						tickFormatter={xAxis.tickFormatter}
						orientation={xAxis.orientation || "bottom"}
						axisLine={xAxis.axisLine || false}
						tickLine={xAxis.tickLine || false}
						padding={xAxis.padding}
						tick={xAxis.tick || {
							fill: "#000000",
							fontSize: 13,
							fontWeight: 700
						}}
					/>

					<YAxis
						yAxisId="left"
						domain={yAxisLeft.domain || [0, "auto"]}
						ticks={yAxisLeft.ticks}
						axisLine={yAxisLeft.axisLine || false}
						tickLine={yAxisLeft.tickLine || false}
						tickFormatter={yAxisLeft.tickFormatter || valueFormatter}
						tick={yAxisLeft.tick || {
							fill: "#000000",
							fontSize: 12
						}}
					/>

					{yAxisRight && (
						<YAxis
							yAxisId="right"
							orientation="right"
							domain={yAxisRight.domain || yAxisLeft.domain || [0, "auto"]}
							ticks={yAxisRight.ticks}
							axisLine={yAxisRight.axisLine || false}
							tickLine={yAxisRight.tickLine || false}
							tickFormatter={yAxisRight.tickFormatter || valueFormatter}
							tick={yAxisRight.tick || {
								fill: "#000000",
								fontSize: 12
							}}
						/>
					)}

					<Tooltip
						content={
							<CustomTooltip valueFormatter={valueFormatter} />
						}
					/>

					{referenceLines.map(line => (
						<React.Fragment key={line.key}>
							<ReferenceLine
								x={line.x}
								y={line.y}
								xAxisId={line.xAxisId || 0}
								yAxisId={line.yAxisId || "left"}
								stroke={line.color || "#8a8a8a"}
								strokeDasharray={line.dash}
								strokeWidth={line.width || 1}
								label={
									line.label
										? (
											<ReferenceLineLabel
												value={line.label}
												position={line.labelPosition || "left"}
												fill={line.labelColor || "#000000"}
												fontSize={line.labelFontSize || 10}
												fontWeight={line.labelFontWeight || 400}
												offset={line.labelOffset || 8}
												verticalOffset={line.labelVerticalOffset || 0}
												tooltip={line.labelTooltip}
												tooltipClassName={line.labelTooltipClassName}
												onTooltipShow={setReferenceTooltip}
												onTooltipHide={() => setReferenceTooltip(null)}
											/>
										)
										: undefined
								}
							/>

							{line.rightLabel && (
								<ReferenceLine
									y={line.y}
									yAxisId={line.yAxisId || "left"}
									stroke="transparent"
									strokeWidth={0}
									label={
										<ReferenceLineLabel
											value={line.rightLabel}
											position={line.rightLabelPosition || "right"}
											fill={line.rightLabelColor || "#000000"}
											fontSize={line.rightLabelFontSize || 10}
											fontWeight={line.rightLabelFontWeight || 400}
											offset={line.rightLabelOffset || 8}
											verticalOffset={line.rightLabelVerticalOffset ?? line.labelVerticalOffset ?? 0}
											tooltip={line.rightLabelTooltip}
											onTooltipShow={setReferenceTooltip}
											onTooltipHide={() => setReferenceTooltip(null)}
										/>
									}
								/>
							)}
						</React.Fragment>
					))}

					{series.map(item => (
						<Line
							key={item.key}
							yAxisId={item.yAxisId || "left"}
							type={item.type || "monotone"}
							dataKey={item.dataKey}
							name={item.name}
							stroke={item.color || "#00518b"}
							strokeWidth={item.width || 2}
							connectNulls={item.connectNulls || false}
							dot={<CustomDot seriesConfig={item} />}
							activeDot={<CustomDot seriesConfig={item} />}
							label={
								item.showLabels && !isTooltipActive
									? (
										<PointLabel
											seriesConfig={item}
											chartData={data}
										/>
									)
									: false
							}
						/>
					))}
				</ComposedChart>
			</ResponsiveContainer>

			{referenceTooltip && (
				<div
					className={[
						"generic-line-chart__reference-tooltip",
						referenceTooltip.className
					].filter(Boolean).join(" ")}
					style={{
						left: referenceTooltip.x,
						top: referenceTooltip.y
					}}
				>
					{referenceTooltip.text}
				</div>
			)}

			{legend.length > 0 && (
				<div
					className="generic-line-chart__legend"
					style={{
						marginLeft: Math.max(0, legendOffset),
						marginRight: chartMargin.right
					}}
				>
					{legend.map(item => (
						<div
							key={item.key}
							className="generic-line-chart__legend-item"
						>
							<span
								className="generic-line-chart__legend-dot"
								style={{
									backgroundColor: item.fill || "transparent",
									borderColor: item.color || item.fill || "#65ad3f"
								}}
							/>

							<span>{item.label}</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default LineChartGeneric;